import type { Sequence } from "../types/Sequence"

/**
 * Tests whether at least one element in a sequence satisfies a predicate function.
 * Returns true if any element passes the test, false otherwise.
 * Default predicate checks truthiness.
 *
 * @param iterable - Sequence of items to test
 * @param func - Predicate function to test each item (default: truthiness check)
 * @returns True if any item satisfies the predicate, false otherwise
 *
 * @example
 * ```ts
 * import { Any } from "@pdfking/tfunc"
 *
 * Any([1, 3, 5], x => x % 2 === 0) // false
 * Any([1, 2, 3], x => x % 2 === 0) // true
 * Any([false, 0, ""]) // false (all falsy)
 * Any([false, 0, "yes"]) // true ("yes" is truthy)
 * ```
 */
export const Any = <T>(
  iterable: Sequence<T>,
  func: (item: T) => boolean = a => (a ? true : false)
) => {
  for (const t of iterable) {
    if (func(t)) return true
  }
  return false
}
